
import React, { Component, ReactNode } from 'react';
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: { componentStack: string } | null;
}

class AppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error) {
    console.error("App Error Boundary caught an error:", error);
    console.error("Error stack:", error.stack);
    console.error("Error name:", error.name);
    console.error("Error message:", error.message);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack: string }) {
    console.error("App Error Boundary detailed error:", error, errorInfo);
    console.error("Component stack:", errorInfo.componentStack);
    this.setState({ errorInfo });
  }

  handleRefresh = () => {
    console.log("Refreshing application");
    window.location.reload();
  };

  handleGoHome = () => {
    console.log("Navigating to home");
    window.location.href = '/';
  };

  handleRetry = () => {
    console.log("Retrying by clearing error state");
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    const { children, fallback } = this.props;
    const { hasError, error, errorInfo } = this.state;

    if (hasError) {
      console.log("Rendering error boundary fallback");
      
      if (fallback) {
        return fallback;
      }

      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background">
          <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-6 border text-center">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mx-auto mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            
            <h2 className="text-xl font-semibold mb-2">Application Error</h2>
            <p className="text-muted-foreground mb-4">
              {error?.message || "Something went wrong with the application."}
            </p>
            
            {process.env.NODE_ENV === 'development' && error?.stack && (
              <details className="mb-4 p-3 bg-muted rounded-md text-left">
                <summary className="cursor-pointer text-sm font-medium mb-2">
                  Error Details
                </summary>
                <pre className="text-xs overflow-auto max-h-32">
                  {error.stack}
                </pre>
              </details>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" onClick={this.handleRetry} className="flex-1">
                Try Again
              </Button>
              <Button variant="outline" onClick={this.handleGoHome} className="flex-1">
                <Home className="h-4 w-4 mr-2" />
                Home
              </Button>
              <Button onClick={this.handleRefresh} className="flex-1">
                <RefreshCw className="h-4 w-4 mr-2" />
                Reload
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default AppErrorBoundary;
